import { t } from '../i18n';
import { localizedErrorMessage } from '../i18n/presentation';
import { MenuGamepadInput } from '../game/input/MenuGamepadInput';
import './contextual-menu.css';

export interface ContextualMenuAction {
  readonly id: string;
  readonly label: string;
  readonly disabled?: boolean;
  readonly primary?: boolean;
  readonly destructive?: boolean;
  readonly run: () => void | Promise<void>;
}

export interface ContextualMenuModel {
  readonly title: string;
  readonly description?: string;
  readonly actions: readonly ContextualMenuAction[];
  readonly initialActionId?: string;
}

let nextMenuId = 0;

/** Native dialogs provide focus containment and make the underlying canvas inert. */
export class ContextualMenu {
  readonly element: HTMLDialogElement;
  readonly #title: HTMLElement;
  readonly #description: HTMLElement;
  readonly #actions: HTMLElement;
  readonly #status: HTMLElement;
  readonly #onBack: () => void;
  readonly #ignoreEscape: () => boolean;
  #signature = '';
  #model: ContextualMenuModel | null = null;
  #pending = false;
  #disposed = false;
  readonly #gamepad = new MenuGamepadInput();
  #frameHandle = 0;

  constructor(host: HTMLElement, onBack: () => void, ignoreEscape: () => boolean = () => false) {
    this.#onBack = onBack;
    this.#ignoreEscape = ignoreEscape;
    const id = `contextual-menu-${nextMenuId++}`;
    this.element = document.createElement('dialog');
    this.element.className = 'contextual-menu';
    this.element.setAttribute('aria-labelledby', `${id}-title`);
    this.element.setAttribute('aria-describedby', `${id}-description`);
    this.#title = document.createElement('h2');
    this.#title.id = `${id}-title`;
    this.#description = document.createElement('p');
    this.#description.id = `${id}-description`;
    this.#actions = document.createElement('div');
    this.#actions.className = 'contextual-menu__actions';
    this.#status = document.createElement('p');
    this.#status.className = 'contextual-menu__status';
    this.#status.setAttribute('role', 'status');
    this.#status.hidden = true;
    this.element.append(this.#title, this.#description, this.#actions, this.#status);
    this.element.addEventListener('cancel', this.#cancel);
    this.element.addEventListener('keydown', this.#keyDown);
    host.append(this.element);
  }

  get open(): boolean { return this.element.open; }

  show(model: ContextualMenuModel): void {
    if (this.#disposed) return;
    this.#model = model;
    const signature = JSON.stringify([model.title, model.description, model.initialActionId,
      model.actions.map(action => [action.id, action.label, action.disabled, action.primary, action.destructive])]);
    const wasOpen = this.open;
    if (signature !== this.#signature && !this.#pending) {
      const focusedId = this.element.contains(document.activeElement)
        ? (document.activeElement as HTMLElement).dataset.action : undefined;
      this.#signature = signature;
      this.#title.textContent = model.title;
      this.#description.textContent = model.description ?? '';
      this.#description.hidden = !model.description;
      this.#status.hidden = true;
      this.#actions.replaceChildren();
      for (const action of model.actions) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = action.label;
        button.dataset.action = action.id;
        button.dataset.primary = String(Boolean(action.primary));
        button.dataset.destructive = String(Boolean(action.destructive));
        button.disabled = Boolean(action.disabled);
        button.addEventListener('click', () => { void this.#run(action.id); });
        this.#actions.append(button);
      }
      if (wasOpen) this.#focusAction(model.initialActionId ?? focusedId);
    }
    if (!wasOpen) {
      this.element.showModal();
      this.#focusAction(model.initialActionId);
      this.#gamepad.reset();
      this.#frameHandle = requestAnimationFrame(this.#pollGamepad);
    }
  }

  hide(): void {
    if (this.#frameHandle) cancelAnimationFrame(this.#frameHandle);
    this.#frameHandle = 0;
    if (this.open) this.element.close();
    this.#model = null;
    this.#signature = '';
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.hide();
    this.element.removeEventListener('cancel', this.#cancel);
    this.element.removeEventListener('keydown', this.#keyDown);
    this.element.remove();
  }

  #focusAction(id?: string): void {
    const buttons = [...this.#actions.querySelectorAll<HTMLButtonElement>('button')];
    (buttons.find(button => button.dataset.action === id && !button.disabled)
      ?? buttons.find(button => !button.disabled))?.focus({ preventScroll: true });
  }

  #moveFocus(direction: -1 | 1): void {
    const buttons = [...this.#actions.querySelectorAll<HTMLButtonElement>('button')].filter(button => !button.disabled);
    if (!buttons.length) return;
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    buttons[(index + direction + buttons.length) % buttons.length]?.focus({ preventScroll: true });
  }

  #pollGamepad = (): void => {
    if (!this.open || this.#disposed) return;
    const action = this.#gamepad.poll();
    if (action === 'previous') this.#moveFocus(-1);
    else if (action === 'next') this.#moveFocus(1);
    else if (action === 'activate' && !this.#pending) (document.activeElement as HTMLElement | null)?.click();
    else if (action === 'back' && !this.#pending) this.#onBack();
    if (this.open && !this.#disposed) this.#frameHandle = requestAnimationFrame(this.#pollGamepad);
  };

  #cancel = (event: Event): void => {
    event.preventDefault();
    if (!this.#pending && !this.#ignoreEscape()) this.#onBack();
  };

  #keyDown = (event: KeyboardEvent): void => {
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !this.#pending) {
      event.preventDefault();
      this.#moveFocus(event.key === 'ArrowUp' ? -1 : 1);
      return;
    }
    if (event.key !== 'Escape' || event.isComposing) return;
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat && !this.#pending && !this.#ignoreEscape()) this.#onBack();
  };

  async #run(id: string): Promise<void> {
    const action = this.#model?.actions.find(candidate => candidate.id === id);
    if (!action || action.disabled || this.#pending || this.#disposed) return;
    try {
      const result = action.run();
      if (!result) return;
      this.#pending = true;
      this.element.setAttribute('aria-busy', 'true');
      for (const button of this.#actions.querySelectorAll<HTMLButtonElement>('button')) button.disabled = true;
      this.#status.textContent = t('common:status.loading');
      this.#status.hidden = false;
      await result;
    } catch (error) {
      if (!this.#disposed) {
        this.#status.textContent = localizedErrorMessage(error);
        this.#status.hidden = false;
      }
    } finally {
      this.#pending = false;
      if (!this.#disposed) {
        this.element.removeAttribute('aria-busy');
        for (const button of this.#actions.querySelectorAll<HTMLButtonElement>('button')) {
          button.disabled = Boolean(this.#model?.actions.find(action => action.id === button.dataset.action)?.disabled);
        }
      }
    }
  }
}
